import { AbstractControl, FormControl, FormArray, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import * as moment from 'moment';
import { MODULE_RFID_KEY_REGEXP } from '../conf/mtma-module';

export class CustomValidators {
	static checkMatchingFields(fieldKey: string, fieldConfirmationKey: string): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const fieldInput = group.controls[fieldKey];
			const fieldConfirmationInput = group.controls[fieldConfirmationKey];
			if (fieldInput.value !== fieldConfirmationInput.value) {
				fieldConfirmationInput.setErrors({ notEquivalent: true });
				return null;
			} else {
				fieldConfirmationInput.setErrors(null);
				return null;
			}
		};
	}

	static checkMatchingFieldsMulti(field1: string, field1Confirma: string, field2: string, field2Confirma: string): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const fieldInput = [];
			const fieldConfirmationInput = [];
			fieldInput[0] = group.controls[field1];
			fieldConfirmationInput[0] = group.controls[field1Confirma];
			fieldInput[1] = group.controls[field2];
			fieldConfirmationInput[1] = group.controls[field2Confirma];

			if (fieldInput[0].value !== fieldConfirmationInput[0].value) {
				fieldConfirmationInput[0].setErrors({ notEquivalentEmail: true });
			} else {
				fieldConfirmationInput[0].setErrors(null);
			}
			if (fieldInput[1].value !== fieldConfirmationInput[1].value) {
				fieldConfirmationInput[1].setErrors({ notEquivalentPassword: true });
			} else {
				fieldConfirmationInput[1].setErrors(null);
			}
			return null;
		};
	}

	static minimumAge(age: number): ValidatorFn {
		return (control: AbstractControl): ValidationErrors => {
			const birthDate = control.value;
			const year = new Date().getFullYear() - age;
			const minDate = new Date().setFullYear(year);
			const notAdult = birthDate > new Date(minDate);
			return notAdult ? { minAge: { value: control.value } } : null;
		};
	}

	static selectorRequiresOne(control: FormControl) {
		if (!control.value || (control.value.length === 0)) {
			return { selectorRequiresOne: true };
		}
		return null;
	}

	static multipleCheckboxRequireOneIO(inputsField: string, outputsField: string) {
		return (group: FormGroup): ValidationErrors => {
			const inputs = group.controls[inputsField];
			const outputs = group.controls[outputsField];
			let valid = false;
			if (inputs.status === 'DISABLED' && outputs.status === 'DISABLED') {
				valid = true;
			}

			for (let x = 0; x < inputs.value.length; ++x) {
				if (inputs.value.at(x)) {
					valid = true;
					break;
				}
			}

			for (let x = 0; x < outputs.value.length; ++x) {
				if (outputs.value.at(x)) {
					valid = true;
					break;
				}
			}

			if (valid) {
				inputs.setErrors(null);
				outputs.setErrors(null);
			} else {
				inputs.setErrors({ required: true });
				outputs.setErrors({ required: true });
			}

			return null;
		};
	}

	static multipleCheckboxRequireOne(fa: FormArray) {
		let valid = false;

		for (let x = 0; x < fa.length; ++x) {
			if (fa.at(x).value) {
				valid = true;
				break;
			}
		}
		return valid ? null : {
			multipleCheckboxRequireOne: true
		};
	}

	static checkCorrectDateSequence(dateField1: string, timeField1: string, dateField2: string, timeField2: string): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const startDate = group.controls[dateField1];
			const startTime = group.controls[timeField1];
			const endDate = group.controls[dateField2];
			const endTime = group.controls[timeField2];

			if (startDate.value === undefined && startDate.value === null) {
				startDate.setErrors({ required: true });
			}
			if (endDate.value === undefined && endDate.value === null) {
				endDate.setErrors({ required: true });
			}
			if (startTime.value === undefined && startTime.value === null) {
				startTime.setErrors({ required: true });
			}
			if (endTime.value === undefined && endTime.value === null) {
				endTime.setErrors({ required: true });
			}
			if (startTime.value !== undefined && endTime.value !== undefined && startTime.value !== null && endTime.value !== null &&
				startDate.value !== undefined && endDate.value !== undefined && startDate.value !== null && endDate.value !== null) {
				const composite1 = moment(startDate.value);
				const composite2 = moment(endDate.value);
				// START
				if (typeof startTime.value === 'string') {
					composite1.hours(+startTime.value.split(':')[0]).minutes(+startTime.value.split(':')[1]);
				} else {
					composite1.hours(+moment(startTime.value).hours()).minutes(+moment(startTime.value).minutes());
				}

				// END
				if (typeof endTime.value === 'string') {
					composite2.hours(+endTime.value.split(':')[0]).minutes(+endTime.value.split(':')[1]);
				} else {
					composite2.hours(+moment(endTime.value).hours()).minutes(+moment(endTime.value).minutes());
				}

				if (composite1.isSameOrAfter(composite2)) {
					endDate.setErrors({ tooEarly: true });
					endTime.setErrors({ tooEarly: true });
				} else {
					endDate.setErrors(null);
					endTime.setErrors(null);
				}
			}
			return null;
		};
	}

	static checkCorrectTimeSequence(timeField1: string, timeField2: string): ValidatorFn {
		if (timeField1 !== null && timeField2 !== null) {
			return (group: FormGroup): ValidationErrors => {
				const startTime = group.controls[timeField1];
				const endTime = group.controls[timeField2];
				if (startTime.value === undefined && startTime.value === null) {
					startTime.setErrors({ required: true });
				}
				if (endTime.value === undefined && endTime.value === null) {
					endTime.setErrors({ required: true });
				}
				if (startTime.value !== undefined && endTime.value !== undefined && startTime.value !== null && endTime.value !== null) {

					const composite1 = moment();
					const composite2 = moment();
					// START
					if (typeof startTime.value === 'string') {
						composite1.hours(+startTime.value.split(':')[0]).minutes(+startTime.value.split(':')[1]);
					} else {
						composite1.hours(+moment(startTime.value).hours()).minutes(+moment(startTime.value).minutes());
					}

					// END
					if (typeof endTime.value === 'string') {
						composite2.hours(+endTime.value.split(':')[0]).minutes(+endTime.value.split(':')[1]);
					} else {
						composite2.hours(+moment(endTime.value).hours()).minutes(+moment(endTime.value).minutes());
					}
					if (composite1.isSameOrAfter(composite2)) {
						endTime.setErrors({ tooEarly: true });
					} else {
						endTime.setErrors(null);
					}
				}
				return null;
			};
		} else {
			return null;
		}
	}

	static checkNotLeadingZero(field: string): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const fieldValue = group.controls[field];
			if (fieldValue.value !== null && fieldValue.value.length > 0 && fieldValue.value[0] === '0') {
				fieldValue.setErrors({ zero: true });
			}
			return null;
		};
	}

	static checkEndHashtag(field: string): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const fieldValue = group.controls[field];
			if (fieldValue.value !== null && fieldValue.value.length > 0 && fieldValue.value[fieldValue.value.length - 1] !== '#') {
				fieldValue.setErrors({ endHashtag: true });
			}
			return null;
		};
	}

	static checkLeadingPlus(field: string): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const fieldValue = group.controls[field];
			if (fieldValue.value === null || fieldValue.value.length === 0 || fieldValue.value[0] !== '+') {
				fieldValue.setErrors({ plus: true });
			}
			//return err;
			return null;
		};
	}

	static checkMatchingMtmaActionContact(fieldAction1: string, fieldAction2: string): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const action1 = group.controls[fieldAction1];
			const action2 = group.controls[fieldAction2];
			if (action1.value !== 255 && action2.value !== 255
				&& action1.value === action2.value) {
				action1.setErrors({ equivalent: true });
				action2.setErrors({ equivalent: true });
			} else {
				action1.setErrors(null);
				action2.setErrors(null);
			}
			//return err;
			return null;
		};
	}

	static checkMatchingMtmaActionTag(fieldAction1: string, fieldAction2: string) {
		return (group: FormGroup) => {
			let err = false;
			const action1 = group.controls[fieldAction1];
			const action2 = group.controls[fieldAction2];
			if (action1.value !== 255 && action2.value !== 255
				&& action1.value === action2.value) {
				action1.setErrors({ equivalent: true });
				action2.setErrors({ equivalent: true });
				err = true;
			} else {
				action1.setErrors(null);
				action2.setErrors(null);
			}
			return err;
		};
	}

	static maxLengthOnByte(field: string, maxlength: number): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const fieldValue = group.controls[field];
			if ((new TextEncoder().encode(fieldValue.value)).length > maxlength) {
				fieldValue.setErrors({ length: true });
			}
			return null;
		};
	}

	static mtmaRFIDConfigValidator(enProtection: string, key: string) {
		return (group: FormGroup) => {
			let err = false;
			const enProtectionValue = group.controls[enProtection];
			const keyValue = group.controls[key];
			if (enProtectionValue.value === 1 && (new TextEncoder().encode(keyValue.value)).length !== 12) {
				keyValue.setErrors({ length: true });
				err = true;
			} else if (new TextEncoder().encode(keyValue.value).length !== 0 && keyValue.value.match(MODULE_RFID_KEY_REGEXP) === null) {
				keyValue.setErrors({ regex: true });
				err = true;
			} else {
				keyValue.setErrors(null);
			}
			return err;
		};
	}

	static checkMinor(field1: string, field2: string): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const num1 = group.controls[field1];
			const num2 = group.controls[field2];
			if (num1.value > num2.value) {
				num1.setErrors({ equivalent: true });
				num2.setErrors({ equivalent: true });
			} else {
				num1.setErrors(null);
				num2.setErrors(null);
			}
			return null;
		};
	}

	static checkMatchingTemporaryRule(fieldRule: string, fieldTime1: string, fieldTime2: string, fieldDate1: string, fieldDate2: string): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const rule = group.controls[fieldRule];
			const time1 = group.controls[fieldTime1];
			const time2 = group.controls[fieldTime2];
			const date1 = group.controls[fieldDate1];
			const date2 = group.controls[fieldDate2];
			let contValue = 0;
			if ((time1.value === null || time1.value === undefined || time1.value === '')) {
				contValue++;
			}
			if ((time2.value === null || time2.value === undefined || time2.value === '')) {
				contValue++;
			}
			if ((date1.value === null || date1.value === undefined)) {
				contValue++;
			}
			if ((date2.value === null || date2.value === undefined)) {
				contValue++;
			}
			if (contValue !== 0 && contValue !== 4) {
				time1.setErrors({ equivalent: true });
				time2.setErrors({ equivalent: true });
				date1.setErrors({ equivalent: true });
				date2.setErrors({ equivalent: true });
			} else if (contValue === 0) {

				const composite1 = moment(date1.value);
				const composite2 = moment(date2.value);
				// START
				if (typeof time1.value === 'string') {
					composite1.hours(+time1.value.split(':')[0]).minutes(+time1.value.split(':')[1]);
				} else {
					composite1.hours(+moment(time1.value).hours()).minutes(+moment(time1.value).minutes());
				}

				// END
				if (typeof time2.value === 'string') {
					composite2.hours(+time2.value.split(':')[0]).minutes(+time2.value.split(':')[1]);
				} else {
					composite2.hours(+moment(time2.value).hours()).minutes(+moment(time2.value).minutes());
				}

				if (composite1.isSameOrAfter(composite2)) {
					date2.setErrors({ tooEarly: true });
					time2.setErrors({ tooEarly: true });
					time1.setErrors(null);
					date1.setErrors(null);
				} else {
					time1.setErrors(null);
					time2.setErrors(null);
					date1.setErrors(null);
					date2.setErrors(null);
				}
			} else {
				time1.setErrors(null);
				time2.setErrors(null);
				date1.setErrors(null);
				date2.setErrors(null);
			}
			//return err;
			return null;
		};
	}

	static checkCorrectOnlyDateEnable(enableField: string, dateField1: string, dateField2: string): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const enable = group.controls[enableField];
			const startDate = group.controls[dateField1];
			const endDate = group.controls[dateField2];

			if (!enable.value) {
				startDate.setErrors(null);
				endDate.setErrors(null);
				return null;
			}

			if (startDate.value === undefined && startDate.value === null) {
				startDate.setErrors({ required: true });
			}
			if (endDate.value === undefined && endDate.value === null) {
				endDate.setErrors({ required: true });
			}
			if (startDate.value !== undefined && endDate.value !== undefined && startDate.value !== null && endDate.value !== null) {
				const composite1 = moment(startDate.value);
				const composite2 = moment(endDate.value);

				if (composite1.isAfter(composite2)) {
					endDate.setErrors({ tooEarly: true });
				} else {
					endDate.setErrors(null);
				}
			}
			return null;
		};
	}

	static checkOneElemOneLists(fieldlist1: string, fieldlist2: string): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const list1 = group.controls[fieldlist1];
			const list2 = group.controls[fieldlist2];
			if (list1.value !== null && list1.value !== undefined && list2.value !== null && list2.value !== undefined &&
				(list1.value.length > 0 || list2.value.length > 0)) {
				list1.setErrors(null);
				list2.setErrors(null);
			} else {
				list1.setErrors({ required: true });
				list2.setErrors({ required: true });
			}
			return null;
		};
	}


	static checkEqualsString(field: string, checkStr: string): ValidatorFn {
		return (group: FormGroup): ValidationErrors => {
			const fieldValue = group.controls[field];
			if (fieldValue.value !== null && fieldValue.value !== checkStr) {
				fieldValue.setErrors({ checkStr: true });
			}
			return null;
		};
	}

}